import React, { useCallback, useEffect } from 'react';
import { ActionIcon, Checkbox, ComboboxItem, ComboboxLikeRenderOptionInput, Group, Select, Table } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useGetDrugFormulations } from '../../../global/entities/Drug/queries/useGetDrugFormulation.tsx';
import { ISimulationDrug } from '../../../global/entities/Simulation/Simulation.ts';
import { useGetDefaultSimulation } from '../../../global/entities/Simulation/queries/useGetDefaultSimulation.tsx.tsx';
import { t } from 'i18next';

import { useSimulation } from '../../../global/hooks/useSimulation.tsx';
import { IconCheck, IconTrash } from '@tabler/icons-react';
import { useTable } from '../../../global/hooks/useTable.tsx';
import { TimeWrapper } from '../../../global/component/molecules/TimeWrapper/TimeWrapper.tsx';
import { PatientStore } from '../../../global/Store/PatientStore.ts';

interface DosageSimulationTableRowProps {
    simulationDrug?: ISimulationDrug;
    index?: number;
    patientStore?: PatientStore;
}

const DosageSimulationTableRow: React.FC<DosageSimulationTableRowProps> = inject('patientStore')(
    observer(({ simulationDrug, index = -1, patientStore }) => {
        const { data: defaultSimulation } = useGetDefaultSimulation(patientStore?.patientId);
        const { data: drugFormulation, refetch: refetchDrugFormulation } = useGetDrugFormulations(
            simulationDrug?.drugID.toString(),
        );
        const { errors } = useSimulation();
        const { sortedData, setSortedData } = useTable();

        useEffect(() => {
            if (simulationDrug?.drugID === 0) 
                return;
            
            refetchDrugFormulation();

            setSortedData((prev: ISimulationDrug[]) => {
                if (!prev.some((drug: ISimulationDrug) => drug.drugID === simulationDrug?.drugID) && simulationDrug) {
                    return [
                        ...prev,
                        {
                            drugID: simulationDrug.drugID!,
                        },
                    ];
                }
                return prev;
            });
        }, [simulationDrug?.drugID]);

        const updateSimulationDrug = (field: string, value: any) => {
            setSortedData((prev) =>
                prev.map((drug) => {
                    if (drug.uniqueId === simulationDrug?.uniqueId) {
                        return {
                            ...drug,
                            [field]: value,
                            ...(field === 'drugID' ? { drugDoseID: 0, drugQuantityID: 0, time: '' } : {}),
                        };
                    }
                    return drug;
                }),
            );
        };

        const renderSelectOption = useCallback(
            ({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
                return (
                    <Group flex="1" gap="xs" style={{ opacity: checked ? 0.5 : 1 }}>
                        {option.label}
                        {checked && <IconCheck style={{ marginInlineStart: 'auto' }} size={16} />}
                    </Group>
                );
            },
            [defaultSimulation?.drugs, sortedData],
        );

        const onClickDelete = () => {
            setSortedData((prev) => prev.filter((drug) => drug.uniqueId !== simulationDrug?.uniqueId));
        };

        return (
            <Table.Tr
                key={`${index}-${simulationDrug?.drugID}`}
                style={{
                    background: index % 2 === 1 ? undefined : 'var(--Background-Gray-light, #E9ECEF)',
                }}
            >
                <Table.Td>
                    <Select
                        placeholder={t('table.dosageSimulation.selectMedication')}
                        data={defaultSimulation?.drugs}
                        value={simulationDrug?.drugID == 0 ? null : simulationDrug?.drugID.toString()}
                        onChange={(value) => {
                            updateSimulationDrug('drugID', value ?? '0');
                        }}
                        renderOption={renderSelectOption}
                        error={errors.find((error) => error.drugId == simulationDrug?.drugID && error.drug)?.drug}
                    />
                </Table.Td>
                <Table.Td>
                    <Select
                        disabled={!simulationDrug?.drugID}
                        placeholder={t('table.dosageSimulation.selectDose')}
                        data={drugFormulation?.drugDoses}
                        value={simulationDrug?.drugDoseID?.toString()}
                        onChange={(value) => updateSimulationDrug('drugDoseID', Number(value))}
                        error={errors.find((error) => error.drugId == simulationDrug?.drugID && error.dose)?.dose}
                    />
                </Table.Td>
                <Table.Td>
                    <Select
                        disabled={!simulationDrug?.drugID}
                        placeholder={t('table.dosageSimulation.selectQuantity')}
                        data={drugFormulation?.drugQuantities}
                        value={simulationDrug?.drugQuantityID?.toString()}
                        onChange={(value) => updateSimulationDrug('drugQuantityID', Number(value))}
                        error={
                            errors.find((error) => error.drugId == simulationDrug?.drugID && error.quantity)?.quantity
                        }
                    />
                </Table.Td>
                <Table.Td>
                    <TimeWrapper
                        value={simulationDrug?.time}
                        onChange={(value) => updateSimulationDrug('time', `${value}`)}
                        error={errors.find((error) => error.drugId == simulationDrug?.drugID && error.time)?.time}
                    />
                </Table.Td>
                <Table.Td>
                    <Checkbox
                        checked={simulationDrug?.meal}
                        onChange={(event) => updateSimulationDrug('meal', event.currentTarget.checked)}
                    />
                </Table.Td>
                <Table.Td>
                    <ActionIcon tabIndex={-1} variant="subtle" color="gray" onClick={() => onClickDelete()}>
                        <IconTrash size={18} />
                    </ActionIcon>
                </Table.Td>
            </Table.Tr>
        );
    }),
);

export default DosageSimulationTableRow;
